import { CompletionService, createCompletionServiceSelector } from '../../features/tracking/services/CompletionService';
import { memoizeWeak } from '../../types/memoizeeWeak';
import { AssertionException } from '../../utils/log';
import { Guid } from '../model/guid';
import { StudentStore } from '../models/Store';
import { CurrentHotspotReducerState } from '../reducers/currentHotspotReducer';
import { CurrentSequencedFlowPositionState } from '../reducers/currentSequencedFlowPositionReducer';
import {
  GetItemById,
  getItemByIdSelector,
  GetItemByIdTyped,
  getItemByIdTypedSelector,
} from '../reducers/items/itemsSelectors';
import {
  BlindMapQuestion,
  Hotspot,
  HotspotMenuContentItem,
  isHotspot,
  isHotspotTab,
  isNonSequencedFlowItem,
  isSection,
  isSequencedFlowItem,
  isSequencedFlowStep,
  MenuContentItem,
  NonSequencedFlowItem,
  Question,
  Section,
  SectionMenuContentItem,
  SequencedFlowItem,
  SequencedFlowStep,
  StartingMessage,
  TrackingStatusEnum,
  TrackingStatus,
} from '@ael/core-api';
import { List } from 'immutable';
import * as memoize from 'memoizee';
import { createSelector } from 'reselect';

const createHotspotMenuContentItem = (
  item: Hotspot,
  trackingStatus: TrackingStatus,
  isActive: boolean,
  isLocked = false,
  title: string
) => {
  return new HotspotMenuContentItem(item, trackingStatus, isActive, isLocked, title);
};

const createMenuContentItem = (
  item: Question | StartingMessage | Hotspot | SequencedFlowStep | BlindMapQuestion | Section,
  trackingStatus: TrackingStatus,
  isActive: boolean,
  isLocked = false
) => {
  return new MenuContentItem(item, trackingStatus, isActive, isLocked);
};

const createSequencedFlowSection = (
  item: Section,
  trackingStatus: TrackingStatus,
  isActive: boolean,
  childItems: List<MenuContentItem>
) => {
  return new SectionMenuContentItem(item, trackingStatus, isActive, childItems);
};

const memoizedCreateHotspotMenuContentItem = memoizeWeak(createHotspotMenuContentItem);
const memoizedCreateMenuContentItem = memoizeWeak(createMenuContentItem);
const memoizedCreateSequencedFlowSection = memoizeWeak(createSequencedFlowSection);

const nonSequencedFlowMenuContentItems =
  (getItemByIdTyped: GetItemByIdTyped, itemEvaluator: CompletionService, currentHotspot: CurrentHotspotReducerState) =>
  (item: NonSequencedFlowItem): List<MenuContentItem> => {
    const hotspots = item.hotspots.map((itemId: string) => {
      const hotspot = getItemByIdTyped(itemId, isHotspot);
      const trackingStatus = itemEvaluator.evaluate(hotspot);
      const isActive = currentHotspot.hotspot ? hotspot.id === currentHotspot.hotspot.id : false;
      const firstHotspotTab = getItemByIdTyped(hotspot.tabs.first(), isHotspotTab);
      return memoizedCreateHotspotMenuContentItem(hotspot, trackingStatus, isActive, false, firstHotspotTab.title);
    });
    const filterByCompleted = (completed: boolean) =>
      hotspots.filter((item: MenuContentItem) => {
        const isCompleted = item.trackingStatus === TrackingStatusEnum.Completed;
        return isCompleted === completed;
      });

    const completedHotspots = filterByCompleted(true);
    const notCompletedHotspots = filterByCompleted(false);
    return notCompletedHotspots.concat(completedHotspots).toList();
  };

const getNonSequencedFlowContentItemsSelector = createSelector(
  getItemByIdTypedSelector,
  createCompletionServiceSelector,
  (store: StudentStore) => store.currentHotspot,
  nonSequencedFlowMenuContentItems
);

const sequencedFlowContentItems =
  (
    getItemById: GetItemById,
    itemEvaluator: CompletionService,
    currentSequencedFlowPosition: CurrentSequencedFlowPositionState
  ) =>
  (item: SequencedFlowItem | Section): List<MenuContentItem> => {
    const childItems = isSection(item) ? item.children : item.contentItems;
    let previousItemIsLocked = false;
    const items = childItems
      .map((itemId: string) => {
        const contentItem = getItemById(itemId);
        if (isSequencedFlowStep(contentItem)) {
          const trackingStatus = itemEvaluator.evaluate(contentItem);
          const isActive = contentItem.id === currentSequencedFlowPosition.current;
          const isLocked = previousItemIsLocked;
          previousItemIsLocked = trackingStatus !== TrackingStatusEnum.Completed;
          return memoizedCreateMenuContentItem(contentItem, trackingStatus, isActive, isLocked);
        }
        if (isSection(contentItem)) {
          const childItems = sequencedFlowContentItems(
            getItemById,
            itemEvaluator,
            currentSequencedFlowPosition
          )(contentItem);
          const trackingStatus = itemEvaluator.evaluate(contentItem);
          const isActive = contentItem.id === currentSequencedFlowPosition.current;
          return memoizedCreateSequencedFlowSection(contentItem, trackingStatus, isActive, childItems);
        }
        throw AssertionException('Invalid item type detected. Item type can only be section or linear flow step');
      })
      .toList();
    return items;
  };

const getSequencedFlowContentItemsSelector = createSelector(
  getItemByIdSelector,
  createCompletionServiceSelector,
  (store: StudentStore) => store.currentSequencedFlowPosition,
  sequencedFlowContentItems
);

const getMenuContentItems = (
  itemId: Guid,
  getItemById: GetItemById,
  getNonSequencedFlowContentItems: (nonSequencedFlow: NonSequencedFlowItem) => List<MenuContentItem>,
  getSequencedFlowContentItems: (sequencedFlow: Section | SequencedFlowItem) => List<MenuContentItem>
): List<MenuContentItem> => {
  const item = getItemById(itemId);

  if (isNonSequencedFlowItem(item)) {
    return getNonSequencedFlowContentItems(item);
  }
  if (isSequencedFlowItem(item) || isSection(item)) {
    return getSequencedFlowContentItems(item);
  }
  throw AssertionException('Not valid content item.');
};

// Retrievers and prepares representation of content items for main navigation for easier retrieval of their properties.
const getMenuContentItemsMemoized = (
  getItem: GetItemById,
  getNonSequencedFlowContentItems: (nonSequencedFlow: NonSequencedFlowItem) => List<MenuContentItem>,
  getSequencedFlowContentItems: (sequencedFlow: Section | SequencedFlowItem) => List<MenuContentItem>
) =>
  memoize((itemId: Guid) =>
    getMenuContentItems(itemId, getItem, getNonSequencedFlowContentItems, getSequencedFlowContentItems)
  );

export const getMenuContentItemsSelector = createSelector(
  getItemByIdSelector,
  getNonSequencedFlowContentItemsSelector,
  getSequencedFlowContentItemsSelector,
  getMenuContentItemsMemoized
);
